/**
 * CompletionStep Component
 *
 * Professional application completion screen with:
 * - Thank you message and confirmation
 * - Next steps and timeline information
 * - Auto-redirect countdown with manual override
 * - Mobile-responsive design
 * - Professional branding and messaging
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, CheckCircle, Clock, Mail } from 'lucide-react';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Chip
} from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';

/**
 * Props for the CompletionStep component
 */
export interface CompletionStepProps {
  /** Application ID for reference */
  applicationId?: string;
  /** Applicant name for personalization */
  applicantName?: string;
  /** Whether to show the auto-redirect countdown */
  showRedirect?: boolean;
  /** Countdown duration in seconds */
  redirectDelay?: number;
  /** Callback when user manually redirects */
  onRedirect?: () => void;
}

/**
 * Completion Step Component
 *
 * Professional completion screen shown after successful beta application submission.
 * Features auto-redirect countdown with manual override option.
 *
 * @param props - CompletionStep configuration
 * @returns Completion screen JSX
 */
export const CompletionStep: React.FC<CompletionStepProps> = ({
  applicantName = 'there',
  showRedirect = true,
  redirectDelay = 8,
  onRedirect
}) => {
  const [countdown, setCountdown] = useState(redirectDelay);
  const [hasRedirected, setHasRedirected] = useState(false);

  /**
   * Handle redirect to main site
   */
  const handleRedirect = useCallback((): void => {
    if (hasRedirected) return;

    setHasRedirected(true);

    // Call onRedirect callback if provided
    if (onRedirect) {
      onRedirect();
    } else {
      // Default redirect to main site
      window.location.href = `https://${Brand.domain}`;
    }
  }, [hasRedirected, onRedirect]);

  // Handle auto-redirect countdown
  useEffect(() => {
    if (!showRedirect || hasRedirected) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showRedirect, hasRedirected, handleRedirect]);

  return (
    <Card className="max-w-2xl mx-auto space-y-6" radius='none' shadow='none' fullWidth>
      {/* Success Header */}
      <CardHeader className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-success-600" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold text-text-primary">
            Application Submitted Successfully!
          </h1>
          <p className="text-lg text-text-secondary">
            Thank you{applicantName !== 'there' ? `, ${applicantName}` : ''}, for applying to the {Brand.shortName} Beta Program.
          </p>
        </div>
      </CardHeader>
      <CardBody>
        {/* Confirmation Details */}
        <Card shadow='sm' radius='sm'>
          <CardHeader className="text-xl font-semibold text-text-primary flex items-center gap-2 border-b border-default-200">
            <Mail className="w-5 h-5 text-primary" />
            What Happens Next
          </CardHeader>
          <CardBody className="space-y-6 p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-semibold text-primary">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-text-primary">Confirmation Email</p>
                    <p className="text-sm text-text-secondary">
                      You'll receive a confirmation email shortly with your application details.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-semibold text-primary">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-text-primary">Review Process</p>
                    <p className="text-sm text-text-secondary">
                      Our team will carefully review your application within <strong>3-5 business days</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-semibold text-primary">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-text-primary">Decision Notification</p>
                    <p className="text-sm text-text-secondary">
                      You'll receive an email with our decision and next steps if selected for the beta program.
                    </p>
                  </div>
                </div>
              </div>


          </CardBody>
          <CardFooter className="space-y-3 border-t border-default-200">
            <div>
              <h3 className="text-lg font-medium text-text-primary flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-warning" />
                Timeline & Expectations
              </h3>

              <div className="flex items-center gap-8">
                <div className="space-y-2">
                  <Chip color="primary" variant="flat" size="sm">
                    Review Period
                  </Chip>
                  <p className="text-sm text-text-secondary">
                    3-5 business days
                  </p>
                </div>

                <div className="space-y-2">
                  <Chip color="success" variant="flat" size="sm">
                    Beta Access
                  </Chip>
                  <p className="text-sm text-text-secondary">
                    Nov/Dec 2025
                  </p>
                </div>

                <div className="space-y-2">
                  <Chip color="secondary" variant="flat" size="sm">
                    Discord Access
                  </Chip>
                  <p className="text-sm text-text-secondary">
                    Upon approval
                  </p>
                </div>
              </div>
            </div>
          </CardFooter>
          
        </Card>
      </CardBody>
      {/* Redirect Section */}
      {showRedirect && (
        <Card>
          <CardBody className="text-center space-y-4 p-6">
            <div className="space-y-2">
              <h3 className="text-lg font-medium text-text-primary">
                Ready to Explore {Brand.shortName}?
              </h3>
              <p className="text-sm text-text-secondary">
                {countdown > 0 ? (
                  <>Redirecting to our main site in <strong>{countdown}</strong> seconds...</>
                ) : (
                  'Redirecting to our main site...'
                )}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                color="primary"
                variant="solid"
                size="lg"
                endContent={<ExternalLink className="w-4 h-4" />}
                onPress={handleRedirect}
                isDisabled={hasRedirected}
              >
                {hasRedirected ? 'Redirecting...' : `Visit ${Brand.name}`}
              </Button>

              {countdown > 0 && (
                <Button
                  color="default"
                  variant="light"
                  size="sm"
                  onPress={() => setCountdown(0)}
                >
                  Skip countdown
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      )}
      <CardFooter className='flex-0 border-t border-default-300 justify-center'>
        <div className="text-center space-y-3">
          <p className="text-sm text-text-secondary">
            We appreciate your interest in becoming part of the {Brand.displayName} Beta community!
          </p>
          <p className="text-xs text-text-tertiary">
            Questions? Contact us at{' '}
            <a
              href={`mailto:${Brand.supportEmail}`}
              className="text-primary hover:text-primary-600 transition-colors"
            >
              {Brand.supportEmail}
            </a>
          </p>
        </div>
      </CardFooter>
    </Card>
  );
};